import { useState } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTranslation } from "../../core/translation.core";
import orderCore from "../../core/order.core";
import { Order } from "../../types/order";

interface DeliveryRecycleToggleProps {
  order: Order;
}

interface DeliveryRecycleFormData {
  deliveryHasRecycle: boolean;
}

export default function DeliveryRecycleToggle({ order }: DeliveryRecycleToggleProps) {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm<DeliveryRecycleFormData>({
    defaultValues: {
      deliveryHasRecycle: order.deliveryHasRecycle || false,
    },
  });

  const handleSubmit = async (data: DeliveryRecycleFormData) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      await orderCore.updateDeliveryRecycle(order.id, data.deliveryHasRecycle, queryClient);
      toast.success(t(msg.import.success));
    } catch (error) {
      toast.error(t(msg.errors.contactAdmin));
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = (checked: boolean) => {
    methods.setValue("deliveryHasRecycle", checked);
    methods.handleSubmit(handleSubmit)();
  };

  const currentValue = methods.watch("deliveryHasRecycle");

  return (
    <form className="flex items-center gap-3 text-sm">
      <span className="text-gray-600 dark:text-gray-400">{t(msg.delivery.deliveryHasRecycle)}:</span>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          {...methods.register("deliveryHasRecycle")}
          onChange={(e) => handleToggle(e.target.checked)}
          disabled={isLoading}
          className="w-4 h-4 text-her-blue border-gray-300 rounded focus:ring-her-blue focus:ring-2 dark:border-gray-600 dark:bg-her-darkSectionBg disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <span
          className={`transition-colors ${
            currentValue ? "text-green-600 dark:text-green-400" : "text-gray-500 dark:text-gray-400"
          }`}
        >
          {currentValue ? t(msg.delivery.recycleRequired) : t(msg.delivery.noRecycleRequired)}
        </span>
        {isLoading && <div className="w-4 h-4 border-2 border-her-blue border-t-transparent rounded-full animate-spin"></div>}
      </label>
    </form>
  );
}
